import React from 'react';

import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { DataReconciliationStep } from '@/modules/common/components/templates/data-reconciliation/components/parts/data-reconciliation-sidebar';
import { useReconciliationNavigation } from '@/modules/common/components/templates/data-reconciliation/hooks/use-reconciliation-navigation';

export interface BottomSectionProps {
  onClickSubmit: () => Promise<void>;
  isLoading: boolean;
  /** Disables the next/finish button (e.g. when there are validation errors) */
  isDisabled?: boolean;
  nextButtonDisabledTooltip?: string;
}

function DataReconciliationBottomSectionForStep(
  currentStep: DataReconciliationStep,
  props: BottomSectionProps
) {
  const { t } = useTranslation([
    'authenticated.dataReconciliation',
    'common',
    'authenticated.onboarding.common',
  ]);

  const { navigateToPreviousStep, previousStep, nextStep } =
    useReconciliationNavigation(currentStep);

  const backButton = previousStep
    ? {
        text: t('stepPage.backButton', {
          stepName: t('entityForBackButton.' + previousStep),
        }),
        onClick: () => navigateToPreviousStep(),
        isDisabled: props.isLoading,
      }
    : undefined;

  const nextButton = {
    text: nextStep ? t('stepPage.nextButton') : t('stepPage.finishButton'),
    onClick: props.onClickSubmit,
    isLoading: props.isLoading,
  };

  return (
    <div className="flex items-center justify-between pt-7">
      <div>
        {backButton && (
          <Button
            type="button"
            variant="outline"
            onClick={backButton.onClick}
            disabled={backButton.isDisabled || nextButton.isLoading}
          >
            {backButton.text}
          </Button>
        )}
      </div>

      {props.nextButtonDisabledTooltip ? (
        <SimpleTooltip content={<p>{props.nextButtonDisabledTooltip}</p>}>
          <span className="inline-flex">
            <ButtonLoadable
              type="button"
              onClick={nextButton.onClick}
              disabled={nextButton.isLoading || props.isDisabled}
              isLoading={nextButton.isLoading}
              icon={<Info className="text-current/80" />}
            >
              {nextButton.text}
            </ButtonLoadable>
          </span>
        </SimpleTooltip>
      ) : (
        <ButtonLoadable
          type="button"
          onClick={nextButton.onClick}
          disabled={nextButton.isLoading || props.isDisabled}
          isLoading={nextButton.isLoading}
        >
          {nextButton.text}
        </ButtonLoadable>
      )}
    </div>
  );
}

/**
 * Data Reconciliation Bottom Section
 * Fixed bottom bar with navigation buttons for data reconciliation steps
 * Similar to OnboardingBottomSection but for reconciliation flow
 */
export function DataReconciliationBottomSection(currentStep: DataReconciliationStep) {
  return (props: BottomSectionProps) => DataReconciliationBottomSectionForStep(currentStep, props);
}
